import type { RenderTextBox, TextRun } from '@lite-workspace/shared/canvas-engine/core';
import { runDropsBoxSlant, runFace, runInstance, type FaceStyle, type RunFace } from '@lite-workspace/text-layout';

const boxFaceStyle = (box: RenderTextBox): FaceStyle => ({
  name: box.postScriptName || box.fontName,
  fontWeight: box.fontWeight,
  italic: box.italic,
});

/** The face a run draws with when it is not the box face (`textLayout` from TEXT_LAYOUT_RUN_FONTS); else `undefined`. */
function ownRunFace(box: RenderTextBox, run: TextRun): RunFace | undefined {
  return runFace(box.textLayout, boxFaceStyle(box), { name: run.fontName, fontWeight: run.fontWeight, italic: run.italic });
}

/**
 * The box as its run asks for a face: the run's font, weight and slant, else the box's, under the instance
 * `runInstance` names (glyph_layout.rs `run_font_variations`). The run's name is the PostScript name an instance is
 * looked up by only while the box keeps its own: the editor drops the box's PostScript name on a weight, slant or
 * family edit, and the run then follows the weight the user chose. A run that changes only the slant keeps the box's
 * name and instance, the slant on top. A run at a weight of its own asks for no instance: the box's name would pin the
 * box's instance over the run's weight.
 */
function runFontBox(box: RenderTextBox, face: RunFace): RenderTextBox {
  const style = { fontWeight: face.fontWeight ?? box.fontWeight, italic: face.italic ?? box.italic };
  switch (runInstance(boxFaceStyle(box), face)) {
    case 'own':
      return { ...box, ...style, fontName: face.ownName ?? '', postScriptName: box.postScriptName ? face.ownName : undefined };
    case 'box':
      return { ...box, ...style };
    case 'none':
      return { ...box, ...style, fontName: box.fontName || box.postScriptName || '', postScriptName: undefined };
  }
}

const faceKey = (face: RunFace): string => `${face.ownName ?? ''}|${face.fontWeight}|${face.italic}`;

/**
 * A face a font resolver loads for a run: `derived` — in the box font at a weight or slant of its own, no name of its
 * own; `upright` — an upright run on the box instance of an italic box, drawn with the instance's slant taken off
 * (`runDropsBoxSlant`, `uprightOf`).
 */
export interface RunFontFace {
  box: RenderTextBox;
  derived: boolean;
  upright: boolean;
}

/**
 * One face per distinct own run face (name, weight, slant) — what a font resolver loads beside the box font. A derived
 * face that resolves to no file leaves its runs on the box face, with no badge and no fallback family; a run in a font
 * of its own that is missing badges its box (print does the same, render.rs `resolve_book_fonts`).
 */
export function runFontFaces(box: RenderTextBox): RunFontFace[] {
  const faces = new Map<string, RunFace>();
  for (const run of box.runs ?? []) {
    const face = ownRunFace(box, run);
    if (face && !faces.has(faceKey(face))) faces.set(faceKey(face), face);
  }

  return [...faces.values()].map(face => ({
    box: runFontBox(box, face),
    derived: !face.ownName,
    upright: runDropsBoxSlant(boxFaceStyle(box), face),
  }));
}

/** One box per distinct own run face — {@link runFontFaces} without the derived mark. */
export function runFontBoxes(box: RenderTextBox): RenderTextBox[] {
  return runFontFaces(box).map(face => face.box);
}

/**
 * The layout font key of a face box; `face.derived` — asked for a derived run face, which a resolver may leave without
 * a key (its face fell back for another box) so the run stays on the box font. Absent — asked for a box.
 */
export type FontRefOf = (box: RenderTextBox, face?: { derived: boolean }) => string | undefined;

/** The box with each own-face run tagged with its layout font key; a run whose face has no key stays on the box font. */
export function withRunFontRefs(box: RenderTextBox, fontRefOf: FontRefOf): RenderTextBox {
  if (!box.runs?.some(run => ownRunFace(box, run))) return box;
  return {
    ...box,
    runs: box.runs.map(run => {
      const face = ownRunFace(box, run);
      const fontRef = face ? fontRefOf(runFontBox(box, face), { derived: !face.ownName }) : undefined;
      return fontRef ? { ...run, fontRef } : run;
    }),
  };
}
